import { ArrowLeftRight } from 'lucide-react'
import { useMemo, useState } from 'react'

import { cn } from '@/lib/utils'
import { useUISettingsStore } from '@/store/useUISettingsStore'

import {
  ALL_SECTIONS,
  buildOccurrenceMap,
  collectGlossary,
  collectRelations,
  findSectionIndex,
  type GlossaryEntry,
  occurrencesOf,
  relationsOf,
} from './book-ai-position'
import { glossaryKindConfig } from './glossary-kinds'
import type { BookAiReaderState } from './useBookAi'

// 今のページ付近とみなす範囲（位置キー）。PDF はそのページ、EPUB は章内の進み具合で前後を少し広めに取る
const EPUB_BEFORE = 0.05
const EPUB_AFTER = 0.15
const MAX_BADGES = 12
// この章数以上前から出てこなかったものに「N章ぶり」を付ける
const LONG_GAP_SECTIONS = 2

type Badge = {
  entry: GlossaryEntry
  /** 初登場なら 'new'、久しぶりなら空いた章数 */
  gap: 'new' | number | null
}

const nearRange = (media: string | null | undefined, key: number): [number, number] =>
  media === 'pdf' ? [Math.floor(key), Math.floor(key) + 1] : [key - EPUB_BEFORE, key + EPUB_AFTER]

const gapLabel = (gap: Badge['gap']) => {
  if (gap === 'new') return '初登場'
  if (typeof gap === 'number') return `${gap}章ぶり`
  return null
}

/**
 * ツールバーの下に、今のページ付近に出てくる人物・用語をバッジで並べる（メニューを出したときだけ表示）
 *
 * 人物を先に、しばらく出てこなかったもの（忘れていそうなもの）を前に並べる。
 * バッジを押すと説明・別名・関係を開く。説明は今読んでいる章までの情報だけを使う。
 */
export const TermBadgeBar = ({ bookAi }: { bookAi: BookAiReaderState }) => {
  const { sections, currentKey, currentSectionIndex, status } = bookAi
  const [openName, setOpenName] = useState<string | null>(null)
  // ネタバレ防止中は今読んでいる章までの説明、切ってあれば本全体の説明を使う
  const spoilerGuard = useUISettingsStore((state) => state.glossarySpoilerGuard)
  const uptoIndex = spoilerGuard ? Math.max(currentSectionIndex, 0) : ALL_SECTIONS

  const entries = useMemo(() => collectGlossary(sections, uptoIndex), [sections, uptoIndex])
  const relations = useMemo(() => collectRelations(sections, uptoIndex), [sections, uptoIndex])

  const badges = useMemo<Badge[]>(() => {
    if (currentKey == null || !status) return []
    const [start, end] = nearRange(status.media, currentKey)
    const occurrences = buildOccurrenceMap(status.term_index)
    const found: Badge[] = []
    for (const entry of entries) {
      const keys = occurrencesOf(occurrences, entry.name)
      if (!keys.some((key) => key >= start && key < end)) continue
      const previous = keys.filter((key) => key < start).at(-1)
      const gapSections = previous == null ? null : currentSectionIndex - findSectionIndex(sections, previous)
      found.push({
        entry,
        gap: previous == null ? 'new' : gapSections != null && gapSections >= LONG_GAP_SECTIONS ? gapSections : null,
      })
    }
    const gapScore = (gap: Badge['gap']) => (gap === 'new' ? 1000 : (gap ?? 0))
    return found
      .sort(
        (a, b) =>
          Number(b.entry.kind === 'person') - Number(a.entry.kind === 'person') || gapScore(b.gap) - gapScore(a.gap),
      )
      .slice(0, MAX_BADGES)
  }, [currentKey, status, entries, sections, currentSectionIndex])

  if (badges.length === 0) return null
  const opened = badges.find((badge) => badge.entry.name === openName)?.entry

  return (
    <div className="relative z-10 border-b border-gray-600 bg-background/85 backdrop-blur-sm">
      <div className="flex gap-1.5 overflow-x-auto px-2 py-1.5">
        {badges.map(({ entry, gap }) => {
          const kind = glossaryKindConfig(entry.kind)
          const label = gapLabel(gap)
          return (
            <button
              key={entry.name}
              type="button"
              className={cn(
                'flex shrink-0 items-center gap-1 rounded-full border px-2 py-0.5 text-xs',
                openName === entry.name ? 'border-primary' : 'border-transparent',
                kind.className,
              )}
              onClick={() => setOpenName((prev) => (prev === entry.name ? null : entry.name))}
            >
              <kind.icon className="size-3" />
              {entry.name}
              {label && <span className="text-[10px] opacity-70">{label}</span>}
            </button>
          )
        })}
      </div>
      {opened && (
        <div className="space-y-1 border-t border-gray-600 px-3 py-2 text-sm">
          {opened.aliases.length > 0 && (
            <p className="text-xs text-muted-foreground">別名: {opened.aliases.join('・')}</p>
          )}
          <p className="leading-relaxed wrap-break-word">{opened.notes.at(-1)?.description}</p>
          {relationsOf(relations, opened).length > 0 && (
            <div className="flex flex-wrap gap-1">
              {relationsOf(relations, opened).map((r) => (
                <span
                  key={`${r.other}-${r.relation}`}
                  className="flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-xs"
                >
                  <ArrowLeftRight className="size-3 text-muted-foreground" />
                  {r.other}
                  <span className="text-muted-foreground">（{r.relation}）</span>
                </span>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
